/**
 * A long block shown by its first lines, with "more" to read the rest.
 *
 * It does not replace a section's own fold: the fold decides whether a block
 * is on the page, this decides how much of an open block a reader sees before
 * they ask for the rest. The toggle appears only when the text actually runs
 * past its lines, measured after layout, so a short block never grows a
 * control it does not need.
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { CLAMP_LESS, CLAMP_MORE } from "./workCopy.js";

export function ClampText({
  lines,
  children,
  className,
  testId,
}: {
  /** How many lines show while the block is clamped. */
  lines: number;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || open) return;
    const measure = () => setOverflows(body.scrollHeight > body.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [open, children]);

  return (
    <div className={"wk-clamp" + (className ? ` ${className}` : "")} data-testid={testId}>
      <div
        ref={bodyRef}
        className={"wk-clamp-body" + (open ? "" : " is-clamped")}
        style={{ "--clamp-lines": lines } as CSSProperties}
        data-testid={testId ? `${testId}-body` : undefined}
      >
        {children}
      </div>
      {(overflows || open) && (
        <button
          type="button"
          className="wk-clamp-more"
          aria-expanded={open}
          data-testid={testId ? `${testId}-more` : "clamp-more"}
          onClick={(event) => {
            // A clamp inside the stage document must not start an edit.
            event.stopPropagation();
            setOpen((value) => !value);
          }}
        >
          {open ? CLAMP_LESS : CLAMP_MORE}
        </button>
      )}
    </div>
  );
}
